import type { Entity } from '@api';
import { startOfDayUTC } from '@chart';
import styles from '@terminal/modules/impacted-companies/components/company-card/CompanyCard.module.scss';
import { PriceChange } from '@terminal/modules/impacted-companies/components/PriceChange';
import { usePriceHistory } from '@terminal/react-query-hooks/prices/usePriceHistory';
import { getEntityPriceVenue } from '@terminal/services/price/price-service';
import clsx from 'clsx';
import { addDays, format, isSameDay, subDays } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  endOfDayUTC,
  getFrozenCatalystPriceChange,
} from './catalyst-price-change-utils';

interface CatalystPriceChangeProps {
  className?: string;
  entity: Entity;
  catalystPublicationDate: string;
  liveDataText: string;
  lastCloseText: string;
}

interface CatalystFrozenPriceChangeProps
  extends Omit<CatalystPriceChangeProps, 'catalystPublicationDate'> {
  publicationDate: Date;
}

const CatalystFrozenPriceChange = ({
  className,
  entity,
  publicationDate,
  liveDataText,
}: CatalystFrozenPriceChangeProps) => {
  const { t } = useTranslation(['insights']);

  const { data: priceHistory } = usePriceHistory({
    entityTag: entity.tag,
    ticker: entity.subscribable_ticker ?? undefined,
    resolution: '1D',
    dateFrom: subDays(startOfDayUTC(publicationDate), 7).toISOString(),
    dateTo: addDays(endOfDayUTC(publicationDate), 7).toISOString(),
    ...getEntityPriceVenue(entity),
  });

  const asOfPublicationDateText = t(
    'insights:catalystGeneration.reflexivityGraph.asOfPublicationDate',
    {
      date: format(publicationDate, 'MMM d'),
    }
  );

  const frozenPriceChange = getFrozenCatalystPriceChange(
    priceHistory,
    publicationDate
  );

  if (!frozenPriceChange) {
    return (
      <PriceChange
        className={className}
        entity={entity}
        shouldDisableLive
        liveDataText={liveDataText}
        lastCloseText={asOfPublicationDateText}
      />
    );
  }

  return (
    <span className={clsx(styles.priceChangeWrapper, className)}>
      <span
        className={clsx(styles.priceChangeContainer, {
          [styles.priceChangeContainerPositive]:
            frozenPriceChange.status === 'positive',
          [styles.priceChangeContainerNegative]:
            frozenPriceChange.status === 'negative',
          [styles.priceChangeContainerDefault]:
            frozenPriceChange.status === 'default',
        })}
      >
        {frozenPriceChange.proportionChange}
      </span>
      <span className={styles.dateLabel}>{asOfPublicationDateText}</span>
    </span>
  );
};

export const CatalystPriceChange = ({
  catalystPublicationDate,
  ...contentProps
}: CatalystPriceChangeProps) => {
  const publicationDate = new Date(catalystPublicationDate);
  const isSamePublicationDay = isSameDay(publicationDate, new Date());

  if (isSamePublicationDay) {
    return <PriceChange {...contentProps} />;
  }

  return (
    <CatalystFrozenPriceChange
      {...contentProps}
      publicationDate={publicationDate}
    />
  );
};
